import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../app/BusinessDash/components/ui/dialog";
import { Clock, Award, GraduationCap, Bell, BookOpen, Leaf, BarChart3, FileText, Brain, Monitor, Target, ClipboardCheck } from "lucide-react";
import { CourseDetailDialog } from "./CourseDetailDialog";
import { cn } from "../../app/BusinessDash/lib/utils";

// Map course IDs to icons and gradient colors for visual variety
const courseVisuals = {
  "sustainable-business-understanding": { icon: BookOpen, gradient: "from-emerald-500 to-teal-600" },
  "green-transition-in-practice": { icon: Leaf, gradient: "from-green-500 to-emerald-600" },
  "carbon-accounting": { icon: BarChart3, gradient: "from-blue-500 to-cyan-600" },
  "final-project": { icon: GraduationCap, gradient: "from-purple-500 to-indigo-600" },
  "sustainability-behavior-design": { icon: Brain, gradient: "from-pink-500 to-rose-600" },
  "sustainable-digital-transformation": { icon: Monitor, gradient: "from-violet-500 to-purple-600" },
  "corporate-sustainability-strategy": { icon: Target, gradient: "from-amber-500 to-orange-600" },
  "esg-reporting": { icon: ClipboardCheck, gradient: "from-sky-500 to-blue-600" },
};

const CourseCardBox = ({ course, onClick }) => {
  const visual = courseVisuals[course.id] || { icon: BookOpen, gradient: "from-gray-500 to-gray-600" };
  const IconComponent = visual.icon;

  return (
    <button
      onClick={onClick}
      disabled={course.isComingSoon}
      className={cn(
        "w-full text-left group rounded-xl border-2 overflow-hidden transition-all duration-200",
        course.isComingSoon
          ? "border-border/30 bg-muted/30 cursor-default opacity-70"
          : "border-border/50 bg-card hover:border-tier-education/40 hover:shadow-lg hover:-translate-y-1"
      )}
    >
      {/* Course image/visual header */}
      <div className={cn(
        "relative h-36 bg-gradient-to-br flex items-center justify-center",
        visual.gradient
      )}>
        <IconComponent className="h-16 w-16 text-white/90" />
        
        {/* Coming soon badge */}
        {course.isComingSoon && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-500 text-white font-medium shadow-md">
              <Bell className="h-3 w-3" />
              Coming soon
            </span>
          </div>
        )}
        
        {/* Elective badge */}
        {course.isElective && !course.isComingSoon && (
          <div className="absolute top-3 right-3">
            <span className="text-xs px-2.5 py-1 rounded-full bg-white/20 text-white font-medium backdrop-blur-sm">
              Elective
            </span>
          </div>
        )}
      </div>

      {/* Course content */}
      <div className="p-4 space-y-3">
        {/* Title */}
        <h3 className={cn(
          "font-semibold text-base leading-tight min-h-[2.5rem] line-clamp-2 transition-colors",
          course.isComingSoon 
            ? "text-muted-foreground" 
            : "text-foreground group-hover:text-tier-education"
        )}>
          {course.title}
        </h3>

        {/* Meta info */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>{course.duration}</span>
          </div>
          <div className="flex items-center gap-1">
            <Award className="h-3.5 w-3.5" />
            <span>{course.ects} ECTS</span>
          </div>
        </div>

        {/* Format badge */}
        <span className={cn(
          "inline-block text-xs px-2 py-0.5 rounded-full font-medium",
          course.isComingSoon
            ? "bg-muted text-muted-foreground/50"
            : "bg-tier-education/10 text-tier-education"
        )}>
          {course.format}
        </span>

        {/* Price and CTA */}
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <div>
            <p className="text-sm font-bold text-foreground">From 3,500 kr</p>
            <p className="text-xs text-muted-foreground">excl. VAT</p>
          </div>
          <div className={cn(
            "text-xs font-medium px-3 py-1.5 rounded-lg transition-colors",
            course.isComingSoon
              ? "bg-muted text-muted-foreground"
              : "bg-tier-education/10 text-tier-education group-hover:bg-tier-education group-hover:text-white"
          )}>
            {course.isComingSoon ? "Get notified" : "View details"}
          </div>
        </div>
      </div>
    </button>
  );
};

export const CourseOverviewDialog = ({
  courses,
  open,
  onOpenChange,
}) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Separate main courses and electives
  const mainCourses = courses.filter(c => !c.isElective);
  const electiveCourses = courses.filter(c => c.isElective);

  const handleCourseClick = (course) => {
    if (course.isComingSoon) return;
    setSelectedCourse(course);
    setDetailDialogOpen(true);
  };

  const handleDetailDialogClose = (open) => {
    setDetailDialogOpen(open);
    if (!open) {
      setSelectedCourse(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-tier-education">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl">Our Courses</DialogTitle>
                <DialogDescription className="text-left">
                  Select a course to see details and sign up
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Course intro text */}
            <div className="p-4 rounded-xl bg-muted/50 border border-border/50">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our courses are at business academy level and can be taken online or in-person in Copenhagen. 
                You can participate individually or as a class. With an exam, you earn ECTS points valid throughout Europe.
              </p>
            </div>

            {/* Pricing overview */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-tier-education/5 border border-tier-education/20 text-center">
                <p className="text-lg font-bold text-tier-education">3,500 kr</p>
                <p className="text-xs text-muted-foreground">Individual Online</p>
              </div>
              <div className="p-3 rounded-xl bg-tier-education/5 border border-tier-education/20 text-center">
                <p className="text-lg font-bold text-tier-education">4,500 kr</p>
                <p className="text-xs text-muted-foreground">Class (CPH/Online)</p>
              </div>
              <div className="p-3 rounded-xl bg-tier-education/5 border border-tier-education/20 text-center">
                <p className="text-lg font-bold text-tier-education">+1,500 kr</p>
                <p className="text-xs text-muted-foreground">With exam (ECTS)</p>
              </div>
            </div>

            {/* Main courses section */}
            <div className="flex items-center gap-3 pt-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Main Courses
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {mainCourses.map((course) => (
                <CourseCardBox
                  key={course.id}
                  course={course}
                  onClick={() => handleCourseClick(course)}
                />
              ))}
            </div>

            {/* Elective courses section */}
            <div className="flex items-center gap-3 pt-4">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Electives
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {electiveCourses.map((course) => (
                <CourseCardBox
                  key={course.id}
                  course={course}
                  onClick={() => handleCourseClick(course)}
                />
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Course detail dialog */}
      <CourseDetailDialog
        course={selectedCourse}
        open={detailDialogOpen}
        onOpenChange={handleDetailDialogClose}
      />
    </>
  );
};






